import { CellValueChangedEvent, ColDef } from '@ag-grid-community/core'
import React, { useCallback, useRef, useState } from 'react'
import { updateCensusFileRows } from '../api/census-enrollment-platform-api-client'
import { CensusFileRowDto } from '../api/dto/census-file-row.dto'
import { CensusFileRowStatusEnum } from '../api/enums/census-file-row-status.enum'
import { CensusFileRowTypeEnum } from '../api/enums/census-file-row-type.enum'
import { PatchActionEnum } from '../api/enums/patch-action.enum'
import DownloadCensusReportButton from '../components/DownloadCensusReportButton'
import InlineEditActionButtons from '../components/InlineEditActionButtons'
import { LoadingOverlay } from '../components/LoadingOverlay/LoadingOverlay'
import ReviewRecordsBase from '../components/ReviewRecordsBase'
import SendSelectedForReviewButton from '../components/SendSelectedForReviewButton'
import ToggleEditModeButton from '../components/ToggleEditModeButton'
import WithdrawSelectedButton from '../components/WithdrawSelectedButton'
import { useMarkRowDirty } from '../hooks/use-mark-row-dirty'
import { CensusFileRowGridData } from '../types/census-file-row-grid-data'
import { configureEditField } from '../utils/configure-edit-field'
import { mapGridDataToCensusFileRowDto } from '../utils/map-grid-data-to-census-file-row-dto'
import { getInProcessPageData } from '../utils/get-in-process-page-data'

const submitCorrections = async (
  rowsToSubmit: CensusFileRowGridData[],
  publicKey: string
) => {
  const updatedRows: CensusFileRowDto[] = rowsToSubmit.map((row) =>
    mapGridDataToCensusFileRowDto(row)
  )
  await updateCensusFileRows(
    updatedRows,
    publicKey,
    PatchActionEnum.UPDATE_RECORDS
  )
}

const ReviewRecordsDraftEmployee = (
  props: Record<string, never> & { routeParams: never }
) => {
  const { markRowDirty, dirtyRows, cleanAllRows, undoLastEdit, editCount } =
    useMarkRowDirty()
  const [mode, setMode] = useState<'edit' | 'submit'>('submit')
  const [loading, setLoading] = useState(false) // Track submission state
  const { importPublicKey: publicKey } = props.routeParams
  const [isSelectAll, setIsSelectAll] = useState(false)
  const allRowsRef = useRef<CensusFileRowGridData[]>([])

  const handleSelectAll = useCallback(
    async (total: number) => {
      try {
        setLoading(true)
        const { data } = await getInProcessPageData({
          limit: total,
          page: 1,
          publicKey,
          censusRowType: CensusFileRowTypeEnum.DRAFT_EMPLOYEE,
          censusFileRowStatuses: [CensusFileRowStatusEnum.IN_PROCESS],
        })
        const allRows: CensusFileRowGridData[] = data.map((d) => ({
          ...d,
          ...d.rowData,
        }))
        allRowsRef.current = allRows
        setIsSelectAll(true)
      } catch (error) {
        console.error('Error fetching all rows for select all:', error)
        setIsSelectAll(false)
      } finally {
        setLoading(false)
      }
    },
    [publicKey]
  )

  const handleCellValueChanged = useCallback(
    (params: CellValueChangedEvent<CensusFileRowGridData>) => {
      markRowDirty(params)
    },
    []
  )

  // Add checkbox column and make cells editable based on mode
  const modifyColDefs = (
    baseColDefs: ColDef<CensusFileRowGridData>[]
  ): ColDef<CensusFileRowGridData>[] => {
    return [
      {
        headerCheckboxSelection: mode === 'submit', // Allow selection only in Submit Mode
        checkboxSelection: mode === 'submit',
        width: 50,
        pinned: 'left',
      },
      ...baseColDefs.map((col) => ({
        ...col,
        editable: mode === 'edit', // Enable editing only in Edit Mode
        ...configureEditField(col?.field, mode), // Apply 'Select' dropdown on fields
      })),
    ]
  }

  return (
    <>
      <LoadingOverlay isLoading={loading} message="Loading..." />
      <ReviewRecordsBase
        tabKey={CensusFileRowTypeEnum.DRAFT_EMPLOYEE}
        {...props}
        rowQueryParams={{
          censusFileRowStatuses: [CensusFileRowStatusEnum.IN_PROCESS],
          censusRowType: CensusFileRowTypeEnum.DRAFT_EMPLOYEE,
        }}
        modifyColDefs={modifyColDefs}
        onSelectionChangedCallback={(selected) => {
          if (
            selected.length === 0 ||
            (isSelectAll && selected.length < allRowsRef.current.length)
          ) {
            setIsSelectAll(false)
          }
        }}
        onSelectAllCallback={handleSelectAll}
        actionButton={({ censusRows, handleSubmit, selectedRows, counts }) => {
          const effectiveSelectedRows = isSelectAll
            ? allRowsRef.current
            : selectedRows
          return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <ToggleEditModeButton
                dirtyRows={dirtyRows}
                selectedRows={effectiveSelectedRows}
                mode={mode}
                setMode={setMode}
              />
              {mode === 'edit' ? (
                <InlineEditActionButtons
                  undoLastEdit={undoLastEdit}
                  editCount={editCount}
                  dirtyRows={dirtyRows}
                  handleSubmit={handleSubmit}
                  cleanAllRows={cleanAllRows}
                  submitCorrections={(rows) =>
                    submitCorrections(rows, publicKey)
                  }
                  updateMode={false}
                  setMode={setMode}
                  setLoading={setLoading}
                />
              ) : (
                <>
                  <SendSelectedForReviewButton
                    publicKey={publicKey}
                    selectedRows={effectiveSelectedRows}
                    handleSubmit={handleSubmit}
                    cleanAllRows={cleanAllRows}
                    setLoading={setLoading}
                    skipConfirmModal
                  />
                  <WithdrawSelectedButton
                    publicKey={publicKey}
                    selectedRows={effectiveSelectedRows}
                    handleSubmit={handleSubmit}
                    cleanAllRows={cleanAllRows}
                    setLoading={setLoading}
                    allRows={censusRows}
                    counts={counts}
                  />
                  <DownloadCensusReportButton
                    publicKey={publicKey}
                    rowType={CensusFileRowTypeEnum.DRAFT_EMPLOYEE}
                  />
                </>
              )}
            </div>
          )
        }}
        handleCellValueChanged={handleCellValueChanged}
      />
    </>
  )
}

export default ReviewRecordsDraftEmployee
